import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@mui/material';
import { Icon, BasicTooltip, toast } from '@livable/saas-react-components';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/stores';
import { AllowedSendersModal } from './AllowedSendersModal';
import { billsPageCompanyId } from './billsPageCompany';
import { BillNotificationSettingsModal } from './BillNotificationSettingsModal';
import useStyles from './billIntakeAliasBannerStyles';

/**
 * The company's bill-intake email address, pinned at the top of the Bills page (LB-5769) — the one
 * surface where a PM discovers where to send bills. Copy is one click; bills sent to this address
 * route to the company automatically (Gate 0 tag routing).
 *
 * Reading never provisions: email intake is a paid feature, so a company without a provisioned
 * alias simply has none and the banner renders nothing. A failed lookup shows the newest address
 * this session has learned for the company, and nothing if it has learned none — the Bills page must
 * not degrade because of it, which is why this uses the silent async: a toast would report an error
 * about something the user never asked for.
 * PM portal only; this must never appear on a tenant-facing surface.
 */
export const BillIntakeAliasBanner = observer(() => {
  const store = useStore();
  const classes = useStyles();
  const _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    alias = _useState2[0],
    setAlias = _useState2[1];
  const _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isSendersOpen = _useState4[0],
    setIsSendersOpen = _useState4[1];
  // The company's bill-notification routing, for the settings modal's prefill and the nudge below.
  const _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    notif = _useState6[0],
    setNotif = _useState6[1];
  const _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    isNotifOpen = _useState8[0],
    setIsNotifOpen = _useState8[1];
  // Whether the alias/config load is in flight — drives the trigger's disabled state and the modal loader.
  const _useState9 = useState(true),
    _useState0 = _slicedToArray(_useState9, 2),
    configLoading = _useState0[0],
    setConfigLoading = _useState0[1];
  // Bumped to re-run the load after a transient failure, without disturbing the selected company.
  const _useState1 = useState(0),
    _useState10 = _slicedToArray(_useState1, 2),
    retryNonce = _useState10[0],
    setRetryNonce = _useState10[1];

  // The single company id every load and save targets -- the same one the toolbar's settings modal acts on.
  const companyId = billsPageCompanyId(store);
  // Moves when the settings modal rotates or generates an address, which this banner otherwise has no
  // way to hear about: the load below re-runs on it (LB-5903).
  const aliasRevision = store.company.billIntakeAliasRevision;
  // Whoever the bill-notifications endpoint will accept a save from -- see the trigger below.
  // On `userProfile`, where the role views live; read off `account` itself it is undefined, which
  // is falsy, which would have hidden the settings from the account holders they are for.
  const isUserPmHolderOrHigher = store.account.userProfile.isUserPmHolderOrHigher;
  // Always the *current* selected company, so an onSaved callback that resolves after a switch compares
  // against the live value rather than the id captured when the save started.
  const companyIdRef = useRef(companyId);
  companyIdRef.current = companyId;
  // The revision the load below last ran for, so it can tell a rotate or generate from a first load.
  const seenRevisionRef = useRef(aliasRevision);

  // Reset when the selected company changes — a switch (which does not unmount the Bills page) must not
  // leave the previous company's alias/config on screen, and must close any modal left open during it
  // (including allowed-senders, whose own load would otherwise read the still-lagging localStorage id).
  useEffect(() => {
    setAlias(null);
    setNotif(null);
    setIsNotifOpen(false);
    setIsSendersOpen(false);
  }, [companyId]);

  // Load alias + notification config. Re-runnable via retryNonce so a transient config failure can be
  // retried from the modal without disturbing the selected company, and on aliasRevision so a rotated or
  // newly generated address replaces the one on screen. The current address stays up while that runs:
  // only a company switch clears it (the reset above), so a rotation never flashes an empty banner.
  useEffect(() => {
    // Whether this run comes from a rotate or generate of this company in this session, rather than a first
    // load or a switch. The revision is shared by every company: a rotation of another company finishing
    // after a switch here moves it too, and must not put this company's cached address up.
    const isAfterChange = aliasRevision !== seenRevisionRef.current && store.company.billIntakeAliasChangedFor === companyId;
    seenRevisionRef.current = aliasRevision;
    if (!companyId) {
      setConfigLoading(false);
      return undefined;
    }
    let active = true;
    setConfigLoading(true);
    // Stands in for a failed read, so a rotation or first generation never needs a reload to show; the store
    // explains why it is the newest address and only this company's (rememberBillIntakeAlias). Read when the
    // failure happens, not before, so nothing learned in between is missed.
    const latestKnownAlias = () => store.company.latestBillIntakeAliases[companyId];

    // After a rotate or generate the new address is known already, so it goes up now rather than when the
    // re-read settles: the HTTP clients set no timeout, and a stalled read would leave the retired address on
    // screen, Copy and all. A first load or a switch still shows nothing it has not read.
    const changedTo = isAfterChange && latestKnownAlias();
    if (changedTo) setAlias(changedTo);
    const load = async () => {
      try {
        const aliasResp = await store.company.getBillIntakeAliasSilentAsync.run({
          companyId
        });
        if (!active) return;

        // A failed read resolves to nothing at all, where "no address" is a response holding null.
        let aliasValue;
        if (aliasResp) {
          aliasValue = aliasResp.response || null;
          store.company.rememberBillIntakeAlias(companyId, aliasValue);
        } else {
          aliasValue = latestKnownAlias() || null;
        }
        setAlias(aliasValue);

        // Only load notification config when there is a provisioned alias — the banner renders nothing
        // without one, so the config would be unused. The read is silent and non-cancelling, so a
        // transient failure shows no toast and never disrupts a company switch's own fetch.
        if (!aliasValue) return;
        const configResp = await store.company.getBillNotificationsConfigSilentAsync.run({
          companyId
        });
        if (!active) return;
        const company = configResp === null || configResp === void 0 ? void 0 : configResp.response;
        setNotif(company ? {
          enabled: company.billNotificationsEnabled === true,
          email: company.billNotificationsEmail || '',
          accountHolderEmail: company.accountHolderEmail || ''
        } : null);
      } catch (_unused) {
        // The silent wrapper can reject on a response-less transport error. Leave notif null (the modal
        // offers Retry) rather than surfacing a toast for a background decoration lookup.
        if (!active) return;
        // Rejected rather than resolved empty, but the same failure: the newest known address stands in. If
        // it was the notification read that failed, the address read before it is that newest one, so
        // nothing on screen changes.
        const known = latestKnownAlias();
        if (known) setAlias(known);
        setNotif(null);
      } finally {
        // Always clear loading — including after a rejection — so the trigger can't stay disabled and
        // the Retry path stays reachable.
        if (active) setConfigLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, retryNonce, aliasRevision]);
  const handleCopy = () => {
    navigator.clipboard.writeText(alias.address).then(() => {
      toast('success', {
        body: ['Successfully copied to clipboard!']
      });
    }).catch(error => {
      toast('error', {
        body: ['Error copying to clipboard', error]
      });
    });
  };
  if (!(alias !== null && alias !== void 0 && alias.address)) return null;
  return (
    /*#__PURE__*/
    // One row (LB-5968): the address with its copy button, a line of what it does, and the actions to the right.
    React.createElement("div", {
      className: classes.banner
    }, /*#__PURE__*/React.createElement("span", {
      className: classes.icon
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "mail"
    })), /*#__PURE__*/React.createElement("div", {
      className: classes.bannerText
    }, /*#__PURE__*/React.createElement("div", {
      className: classes.addressRow
    }, /*#__PURE__*/React.createElement("span", {
      className: classes.label
    }, "Email bills to"), /*#__PURE__*/React.createElement("span", {
      className: classes.address
    }, alias.address), /*#__PURE__*/React.createElement(BasicTooltip, {
      title: "Copy to clipboard",
      placement: "top"
    }, /*#__PURE__*/React.createElement(Button, {
      variant: "actionIcon",
      size: "small",
      className: classes.copy,
      onClick: handleCopy
    }, /*#__PURE__*/React.createElement(Icon, {
      type: "document-file-copy"
    })))), /*#__PURE__*/React.createElement("span", {
      className: classes.hint
    }, "They are routed to your company automatically.", notif && !notif.enabled && ' Bill updates aren’t emailed yet — set a notifications email to get them.')), /*#__PURE__*/React.createElement(AllowedSendersModal, {
      isOpen: isSendersOpen,
      handleOpen: () => setIsSendersOpen(true),
      handleClose: () => setIsSendersOpen(false),
      trigger: /*#__PURE__*/React.createElement(Button, {
        variant: "actionButtonInvert",
        onClick: () => setIsSendersOpen(true)
      }, "Manage allowed senders")
    }), /*#__PURE__*/React.createElement(BillNotificationSettingsModal, {
      isOpen: isNotifOpen,
      handleOpen: () => setIsNotifOpen(true),
      handleClose: () => setIsNotifOpen(false),
      config: notif,
      companyId: companyId,
      loading: configLoading,
      onRetry: () => setRetryNonce(n => n + 1),
      onSaved: (savedCompanyId, saved) => {
        // Ignore a save that resolved after a company switch — compare against the live selection
        // (via ref), not the id captured when this callback was created.
        if (savedCompanyId === companyIdRef.current) {
          setNotif(prev => _objectSpread(_objectSpread({}, prev || {}), {}, {
            enabled: saved.enabled,
            email: saved.email
          }));
        }
      },
      trigger:
      // Offered only to whoever the API will accept a save from (LB-5871).
      //
      // Where bill mail goes is one setting for the whole company, and the endpoint behind this
      // is PmHolderOrHigher. A Regular PM was shown the button, the modal and the Save, and got
      // a 403 and a toast carrying a bare error code -- with the setting unchanged, which the
      // toast did not say. `isUserPmHolderOrHigher` is the store's own mirror of that policy,
      // and gating on it is how this page is meant to agree with the API about who gets in.
      //
      // Hidden rather than disabled, as with the settings button on the toolbar: a Regular PM
      // cannot become the account holder, so a greyed-out control would only ever be a dead end.
      isUserPmHolderOrHigher ?
      /*#__PURE__*/
      // Disabled only while the initial load is in flight; a failed load still opens (the modal
      // offers a retry) rather than leaving the button stuck disabled forever.
      React.createElement(Button, {
        variant: "actionButtonInvert",
        onClick: () => setIsNotifOpen(true),
        disabled: configLoading
      }, "Notification settings") : null
    }))
  );
});